/**
 * @fileoverview Route-level permission guard.
 *
 * แก้ปัญหา: user ที่ไม่มี permission ของเมนู ยังสามารถพิมพ์ URL เข้าหน้าได้โดยตรง
 * (เมนูถูกซ่อนแล้วจาก Navbar แต่ route ไม่ได้ตรวจสิทธิ์)
 *
 * วิธีการ: ใช้ "แหล่งความจริงเดียว" (single source of truth) คือ map ของ
 * link -> permission ที่ประกาศไว้แล้วใน global.js (global.menu_admin/customer/
 * warehouse/super_user) มาตรวจสิทธิ์ตอนเข้าถึง route โดยตรง
 *
 * ใช้เป็น layout route ของ react-router v6:
 *   <Route element={<PermissionRoute />}>
 *     <Route path="/ad/dashboard" element={<AdminDashboard />} />
 *     ...
 *   </Route>
 */

import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
// ensure global.menu_* are populated (idempotent import)
import "../global.js";

/**
 * สร้าง map ของ pathname -> permission ที่ต้องใช้ จากนิยามเมนูใน global.js
 * @returns {Object<string,string>}
 */
const buildPermissionMap = () => {
  const map = {};
  const menus = [
    ...(global.menu_admin || []),
    ...(global.menu_customer || []),
    ...(global.menu_warehouse || []),
    ...(global.menu_super_user || []),
  ];
  menus.forEach((item) => {
    if (item.link && item.permission) {
      const path = item.link.split("?")[0]; // ตัด query string ออก
      map[path] = item.permission;
    }
  });
  return map;
};

/**
 * พอร์ทัลที่แต่ละ path prefix สังกัดอยู่
 * @type {Object<string,string>}
 */
const PORTAL_BY_PREFIX = {
  "/ad/": "admin",
  "/cs/": "customer",
  "/wh/": "warehouse",
  "/su/": "super_user",
  "/mb/": "mobile",
};

/**
 * ประเภทผู้ใช้ที่ถูกล็อกให้เข้าได้เฉพาะบางพอร์ทัล ไม่ว่า role จะมี permission หรือไม่ก็ตาม
 * ช่างเทคนิคใช้ได้เฉพาะพอร์ทัลมือถือ ห้ามเข้าพอร์ทัลแอดมิน/ลูกค้า/คลังสินค้า
 * @type {Object<string,{portals: string[], fallback: string}>}
 */
const PORTAL_LOCK_BY_USER_TYPE = {
  technician: { portals: ["mobile"], fallback: "/mb/mobile" },
};

/**
 * อ่าน profile จาก localStorage (ค่าล่าสุดเสมอ)
 * @returns {Object|null}
 */
const getProfile = () => {
  try {
    return JSON.parse(localStorage.getItem("cm-profile"));
  } catch {
    return null;
  }
};

const PermissionRoute = () => {
  const location = useLocation();
  const token = localStorage.getItem("cm-acc-tk");
  const profile = getProfile();

  // 1) ยังไม่ได้ login -> ไปหน้า login
  if (!token || !profile) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // 2) บัญชีระดับแพลตฟอร์ม/บริษัท ข้ามการตรวจสิทธิ์ระดับหน้า
  //    (สอดคล้องกับการ special-case ที่มีอยู่เดิมใน Navbar/console)
  if (["super_user", "admin-company"].includes(profile.user_type)) {
    return <Outlet />;
  }

  // 3) ประเภทผู้ใช้ที่ถูกล็อกพอร์ทัล -> เข้าได้เฉพาะพอร์ทัลของตัวเอง แม้ role จะมี permission
  //    (กันกรณีพิมพ์ URL ตรง ๆ หรือถูกกำหนด role ที่มีสิทธิ์กว้างเกินไป)
  const portalLock = PORTAL_LOCK_BY_USER_TYPE[profile.user_type];
  if (portalLock) {
    const prefix = Object.keys(PORTAL_BY_PREFIX).find((item) => location.pathname.startsWith(item));
    const portal = prefix ? PORTAL_BY_PREFIX[prefix] : null;
    if (portal && !portalLock.portals.includes(portal)) {
      return <Navigate to={portalLock.fallback} replace />;
    }
  }

  const required = buildPermissionMap()[location.pathname];

  // 4) ไม่มีการประกาศ permission สำหรับ path นี้ -> ปล่อยผ่าน (เช่นหน้าที่ไม่อยู่ในเมนู)
  if (!required) {
    return <Outlet />;
  }

  // 5) ตรวจสิทธิ์จาก permission ของ role
  const perms = Array.isArray(profile?.role?.permission) ? profile.role.permission : [];
  if (perms.includes(required)) {
    return <Outlet />;
  }

  // 6) ไม่มีสิทธิ์ -> หน้า 403 (ไม่ใช่ 404 เพราะหน้ามีอยู่จริง แค่ role ไม่ถึง)
  //    แยกให้ชัดว่าเป็นปัญหาสิทธิ์ ไม่ใช่ปัญหา route หาย
  return <Navigate to="/forbidden" replace />;
};

export default PermissionRoute;
